import Link from '@/components/_rebrand/Link';
import cn from 'classnames';
import React from 'react';
import IconArrowFooterLink from '../IconArrowFooterLink';
import IconLogo from '../IconLogo';

type TopSectionProps = {
  backgroundColor: 'violet';
  tagline: string;
  badgeIcons: React.ReactNode[];
  socialLinks: {
    title: string;
    href: string;
    icon: React.ReactNode;
  }[];

  linkColumns: {
    sections: {
      title: string;
      links: {
        title: string;
        href: string;
        tag?: string;
      }[];
    }[];

    featuredLinks?: {
      title: string;
      href: string;
    }[];
  }[];

}

export const TopSection = ({ backgroundColor, tagline, badgeIcons, socialLinks, linkColumns }: TopSectionProps) => {
  return (
    <div
      className={cn('px-container-mobile md:px-container-desktop py-4 md:py-10', {
        'bg-violet-800 text-violet-200': backgroundColor === 'violet',
      })}
    >
      <div className="mx-auto max-w-grid">
        <div className="grid grid-cols-12 gap-y-8 gap-gutter-mobile md:gap-x-gutter-desktop">
          <div className="col-span-12 xl:col-span-4 flex flex-col gap-2">
            <IconLogo
              className={cn('w-[282px] h-[65px] block', {
                'text-violet-100': backgroundColor === 'violet',
              })}
            />
            <p className="text-30/36 max-w-44">{tagline}</p>

            <div className="flex gap-2">{badgeIcons}</div>

            <ul className="flex gap-2">
              {socialLinks.map((link) => (
                <li key={`footer-social-link-${link.href}`}>
                  <Link
                    title={link.title}
                    href={link.href}
                    target="_blank"
                    className={cn(
                      'flex flex-col justify-center items-center w-6 h-6 rounded-full duration-200 transition-colors ease-in-quad',
                      {
                        'bg-violet-700 hover:bg-opacity-50': backgroundColor === 'violet',
                      },
                    )}
                  >
                    {link.icon}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-12 xl:col-span-8">
            <div>
              {linkColumns.map((column, index) => (
                <div key={`footer-link-column-${index}`} className="flex flex-col gap-2">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-y-5 gap-x-gutter-mobile md:gap-x-gutter-desktop">
                    {column.sections.map((section) => (
                      <div key={`footer-link-column-section-${section.title?.replace(/\s/g, '-') ?? index}`} className="flex flex-col gap-3">
                        <p className="text-10/14 uppercase text-white">{section.title}</p>
                        <ul className="flex flex-col gap-2">
                          {section.links.map((link) => (
                            <li key={`footer-link-${link.href}`}>
                              <Link
                                href={link.href}
                                className={cn(
                                  'hover:text-white duration-200 transition-colors ease-in-quad flex gap-2',
                                  {
                                    'text-violet-200': backgroundColor === 'violet',
                                  },
                                )}
                              >
                                <span className="block">{link.title}</span>
                                {link.tag && (
                                  <span className="relative top-[1px] bg-green-200 text-green-800 block rounded-4 px-half text-10/15 h-[15px] uppercase font-mono">
                                    {link.tag}
                                  </span>
                                )}
                              </Link>
                            </li>
                          ))}
                        </ul>

                      </div>
                    ))}

                    {column.featuredLinks && (
                      <ul className="flex flex-col gap-2">
                        {column.featuredLinks.map((link) => (
                          <li key={`footer-link-${link.href}`}>
                            <Link
                              href={link.href}
                              className={cn(
                                'hover:text-white duration-200 transition-colors ease-in-quad flex items-center gap-1',
                                {
                                  'text-violet-100': backgroundColor === 'violet',
                                },
                              )}
                            >
                              <span className="block">{link.title}</span>
                              <IconArrowFooterLink className="block" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
